import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';

import { EnvironmentConfig } from '../../../client-config/index';
import { CmsService } from '../../../core/services/cms/cms.service';
import { ISystemConfig } from '../../../core/services/cms/models';
import { PubSubService } from '../../../core/services/communication/pubsub/pubsub.service';
import { DeviceService } from '../../../core/services/device/device.service';
import { RemoteBetslipService } from '../../../core/services/remoteBetslip/remote-betslip.service';
import { SessionStorageService } from '../../../core/services/storage/session-storage.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { UserService } from '../../../core/services/user/user.service';
import { WindowRefService } from '../../../core/services/windowRef/window-ref.service';
import { AWSFirehoseService } from '../../../lazy-modules/awsFirehose/service/aws-firehose.service';
import { DEFAULT_FLAG_VARIATIONS, FEATURE_FLAGS, NO_FEATURE_STATE } from '../../../lazy-modules/flagSource/constants/flag-source.constants';
import { FlagUtils } from '../../../lazy-modules/flagSource/models/flag.utils';
import { IQuickbetSelectionResponseModel } from '../../../quickbet/models/quickbet-selection-response.model';

@Component({
    selector: 'quickbet-panel-wrapper',
    templateUrl: './quickbet-panel-wrapper.component.html',
    standalone: false,
})
export class QuickbetPanelWrapperComponent implements OnInit, OnDestroy {
    selection: IQuickbetSelectionResponseModel;
    quickbetShown: boolean;
    selections: any;
    quaccabar: boolean = false;
    private readonly environment = inject(EnvironmentConfig).configuration;
    brand: string = this.environment.BRAND ?? '';
    isLadbrokes: boolean;
    quaccaEnabled = signal<boolean>(false);
    // 'quaccaLdEnabled' temporary should be removed while LD descope for quacca.
    quaccaLdEnabled = signal<boolean>(false);
    hideQuaccaOnStream = signal<boolean>(false);
    constructor(
        protected pubsub: PubSubService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected cmsService: CmsService,
        protected remoteBsService: RemoteBetslipService,
        protected sessionStorageService: SessionStorageService,
        protected awsService: AWSFirehoseService,
        protected windowRef: WindowRefService,
        protected storageService: StorageService,
        protected userService: UserService,
        protected deviceService: DeviceService,
    ) {
        this.renderQuickbet = this.renderQuickbet.bind(this);
        this.isLadbrokes = this.brand === 'ladbrokes';
        this.ldSubscription();
    }

    ngOnInit(): void {
        this.addListeners();
        this.cmsService.getSystemConfig().subscribe((config: ISystemConfig) => {
            const quickBetCmsConfig = config.quickBet || {};
            this.quaccaEnabled.set(config.Quaccabar?.enabled);
            this.ldHandler(this.getVariant());
            if (quickBetCmsConfig.EnableQuickBet && !this.quaccaLdEnabled()) {
                // Check if session with RemoteBetslip MS should be restored for Quickbet Betslip
                this.remoteBsService.restoreSession();
            }
        });
    }

    addListeners() {
        this.pubsub.subscribe('QuickbetPanelWrapperComponent', this.pubsub.API.RENDER_QUICKBET_COMPONENT, this.renderQuickbet);

        this.pubsub.subscribe(
            'quacca',
            this.pubsub.API.RENDER_QUACCA_BAR_COMPONENT,
            (data: { singles: any[]; multiples: any[]; byb: any[]; bybPlus: any[] }) => {
                this.selections = [...data.singles, ...data.multiples, ...data.byb, ...data.bybPlus];
                this.quaccabar = true;
            },
        );

        this.pubsub.subscribe('tote', this.pubsub.API.BSREVAMP_TOTEBETDATA, (totebetData: any[]) => {
            this.selections = totebetData;
        });

        /*
         * Using below pubsub('show-slide-out-betslip') to show quaccabar on overask scenario false case after closing betslip.
         */
        this.pubsub.subscribe('quacca', 'show-slide-out-betslip', (value: boolean) => {
            !value && !this.quaccaLdEnabled() && this.ldHandler(this.getVariant());
        });

        this.pubsub.subscribe('quacca', 'stream_landscape', (flag: boolean) => {
            this.hideQuaccaOnStream.set(flag);
        });
    }

    ngOnDestroy(): void {
        this.pubsub.unsubscribe('QuickbetPanelWrapperComponent');
    }

    protected renderQuickbet(selection: IQuickbetSelectionResponseModel): void {
        this.selection = selection;
        this.quickbetShown = true;
        this.changeDetectorRef.detectChanges();
    }

    ldSubscription() {
        /**subscription triggers only on page reload and flag value change*/
        this.pubsub.subscribe('qb-panel', [this.pubsub.API.FLAG_LD_INIT, this.pubsub.API.FLAG_IDENTIFY_INIT], () => {
            this.ldHandler(this.getVariant());
        });

        this.pubsub.subscribe('qb-panel', `FLAG_UPDATE_${FEATURE_FLAGS.quaccabar}`, () => {
            this.awsService.addAction('quaccabar-Application full reload initiated');
            this.windowRef.nativeWindow.location.reload();
        });
    }
    /**
     * Methot to get latest flag variation
     */
    getVariant(): boolean {
        return this.windowRef.nativeWindow.ldClient
            ? FlagUtils.getFlagVariation(FEATURE_FLAGS.quaccabar, DEFAULT_FLAG_VARIATIONS.quaccabar) || NO_FEATURE_STATE
            : Boolean(DEFAULT_FLAG_VARIATIONS.quaccabar);
    }

    ldHandler(val: boolean) {
        const isTabOrDesktop = !this.deviceService.isMobile || this.deviceService.isDesktop;
        this.quaccaLdEnabled.set(!this.isOverAskActive() && this.quaccaEnabled() && !!val && !isTabOrDesktop);
        this.sessionStorageService.set('showBetslip', !this.quaccaLdEnabled());
        const isBetslipOpened = document.getElementsByClassName('is-visible').length > 0;
        this.pubsub.publish(this.pubsub.API['show_betslip'], isBetslipOpened || !this.quaccaLdEnabled()); //!this.quaccaEnabled);
    }

    isOverAskActive(): boolean {
        const overAskUser = this.storageService.get('overaskUsername');
        const overaskIsInProcess = this.storageService.get('overaskIsInProcess');
        return overAskUser === this.userService.username && overaskIsInProcess;
    }
}
